import React, { createContext, useContext, useCallback, useRef } from 'react';

const VideoSyncContext = createContext();

export const useVideoSync = () => {
  const context = useContext(VideoSyncContext);
  if (!context) {
    throw new Error('useVideoSync must be used within a VideoSyncProvider');
  }
  return context;
};

export const VideoSyncProvider = ({ children }) => {
  const videoPlayersRef = useRef(new Map()); // dashboardId -> player info
  const masterTimeRef = useRef(0);
  const isPlayingRef = useRef(false);

  // Register a video player
  const registerVideoPlayer = useCallback((dashboardId, playerInfo) => {
    videoPlayersRef.current.set(dashboardId, {
      ...playerInfo,
      lastUpdate: Date.now()
    });
    console.log(`📹 Registered video player: ${dashboardId}`, playerInfo);
  }, []);

  // Unregister a video player
  const unregisterVideoPlayer = useCallback((dashboardId) => {
    videoPlayersRef.current.delete(dashboardId);
    console.log(`📹 Unregistered video player: ${dashboardId}`);
  }, []);

  // Find the closest telemetry point to a given time
  const findClosestTelemetryPoint = useCallback((telemetryData, targetTime) => {
    if (!telemetryData || telemetryData.length === 0) return null;
    
    let closest = telemetryData[0];
    let minDistance = Math.abs(telemetryData[0].time - targetTime);
    
    for (const point of telemetryData) {
      const distance = Math.abs(point.time - targetTime);
      if (distance < minDistance) {
        minDistance = distance;
        closest = point;
      }
    }
    
    return closest;
  }, []);

  // Calculate distance between two GPS points
  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  }, []);

  // Sync all videos to a specific time
  const syncAllVideosToTime = useCallback((targetTime) => {
    masterTimeRef.current = targetTime;
    
    videoPlayersRef.current.forEach((playerInfo, dashboardId) => {
      if (playerInfo.seekTo && typeof playerInfo.seekTo === 'function') {
        try {
          playerInfo.seekTo(targetTime);
          console.log(`📹 Synced video ${dashboardId} to time ${targetTime}s`);
        } catch (error) {
          console.error(`❌ Failed to sync video ${dashboardId}:`, error);
        }
      }
    });
  }, []);

  // Sync all videos to a GPS position
  const syncAllVideosToGPS = useCallback((targetLat, targetLon, tolerance = 50) => {
    console.log(`🗺️ Syncing all videos to GPS: ${targetLat}, ${targetLon}`);
    
    videoPlayersRef.current.forEach((playerInfo, dashboardId) => {
      if (playerInfo.telemetryData && playerInfo.telemetryData.length > 0) {
        // Find the closest telemetry point to the target GPS position
        let closestPoint = null;
        let minDistance = Infinity;
        
        for (const point of playerInfo.telemetryData) {
          if (point.lat && point.lon) {
            const distance = calculateDistance(targetLat, targetLon, point.lat, point.lon);
            if (distance < minDistance && distance <= tolerance) {
              minDistance = distance;
              closestPoint = point;
            }
          }
        }
        
        if (closestPoint && playerInfo.seekTo) {
          try {
            playerInfo.seekTo(closestPoint.time);
            console.log(`📹 Synced video ${dashboardId} to GPS point at time ${closestPoint.time}s (distance: ${minDistance.toFixed(1)}m)`);
          } catch (error) {
            console.error(`❌ Failed to sync video ${dashboardId} to GPS:`, error);
          }
        }
      }
    });
  }, [calculateDistance]);

  // Play all videos
  const playAllVideos = useCallback(() => {
    isPlayingRef.current = true;
    
    videoPlayersRef.current.forEach((playerInfo, dashboardId) => {
      if (playerInfo.play && typeof playerInfo.play === 'function') {
        try {
          playerInfo.play();
          console.log(`▶️ Playing video ${dashboardId}`);
        } catch (error) {
          console.error(`❌ Failed to play video ${dashboardId}:`, error);
        }
      }
    });
  }, []);

  // Pause all videos
  const pauseAllVideos = useCallback(() => {
    isPlayingRef.current = false;
    
    videoPlayersRef.current.forEach((playerInfo, dashboardId) => {
      if (playerInfo.pause && typeof playerInfo.pause === 'function') {
        try {
          playerInfo.pause();
          console.log(`⏸️ Paused video ${dashboardId}`);
        } catch (error) {
          console.error(`❌ Failed to pause video ${dashboardId}:`, error);
        }
      }
    });
  }, []);

  // Get current master time
  const getMasterTime = useCallback(() => {
    return masterTimeRef.current;
  }, []);

  // Get playing state
  const getIsPlaying = useCallback(() => {
    return isPlayingRef.current;
  }, []);

  // Get all registered video players
  const getVideoPlayers = useCallback(() => {
    return Array.from(videoPlayersRef.current.entries()).map(([id, info]) => ({
      dashboardId: id,
      ...info
    }));
  }, []);

  const contextValue = {
    registerVideoPlayer,
    unregisterVideoPlayer,
    syncAllVideosToTime,
    syncAllVideosToGPS,
    playAllVideos,
    pauseAllVideos,
    getMasterTime,
    getIsPlaying,
    getVideoPlayers,
    findClosestTelemetryPoint,
    calculateDistance
  };

  return (
    <VideoSyncContext.Provider value={contextValue}>
      {children}
    </VideoSyncContext.Provider>
  );
};

export default VideoSyncProvider;
